import { guardedLocalStorage } from './guardedLocalStorage';

/**
 * What a person typed here and did not send — task drafts, trigger drafts, the
 * builder's backups — is theirs alone (spec 197). A browser can be shared: it
 * is offered back to whoever made it, never to the next person to sign in.
 *
 * The stores take their storage keys from here, so this module can empty
 * their storage without loading them. A store already in memory also
 * registers how it empties itself: removing its key alone would leave its
 * state in this tab.
 */
export const TASK_DRAFTS_STORAGE_KEY = 'product-x-next-task-drafts';
export const TRIGGER_DRAFTS_STORAGE_KEY = 'product-x-next-trigger-drafts';
export const BUILDER_BACKUPS_STORAGE_KEY = 'product-x-next-builder-backups';
/** Whose drafts this browser holds. */
export const DRAFTS_OWNER_STORAGE_KEY = 'product-x-next-drafts-owner';

const PERSONAL_KEYS = [
  TASK_DRAFTS_STORAGE_KEY,
  TRIGGER_DRAFTS_STORAGE_KEY,
  BUILDER_BACKUPS_STORAGE_KEY,
];

const loaded = new Set<() => void>();

/** Called once by each personal store as its module loads. */
export function registerPersonalDraftStore(clear: () => void): void {
  loaded.add(clear);
}

/**
 * Every draft, everyone's: the browser is being handed back (sign out, Switch
 * account). Storage first, then the stores in memory, whose persistence then
 * writes their emptied state.
 */
export function clearPersonalDrafts(): void {
  for (const key of PERSONAL_KEYS) guardedLocalStorage.removeItem(key);
  for (const clear of loaded) clear();
  guardedLocalStorage.removeItem(DRAFTS_OWNER_STORAGE_KEY);
}

/**
 * The signed-in person is known. Drafts left by someone else go; the same
 * person's stay. A browser that never recorded an owner keeps what it holds:
 * there is no sign it was anyone else's.
 */
export function handDraftsOver(userId: string): void {
  const previous = guardedLocalStorage.getItem(DRAFTS_OWNER_STORAGE_KEY);
  if (typeof previous === 'string' && previous !== userId)
    clearPersonalDrafts();
  guardedLocalStorage.setItem(DRAFTS_OWNER_STORAGE_KEY, userId);
}
